<script setup lang="ts">
import { ref, computed } from 'vue';
import { useProductStore } from '../stores';
import { useLaboratoryStore } from '../stores/laboratoryStore';
import { useCategoryStore } from '../stores/categoryStore';
import { useNotificationStore } from '../stores/notificationStore';
import UpdateOptions from './bulk-price-update/UpdateOptions.vue';
import type { ProductWithDetails } from '../types';

// Stores
const productStore = useProductStore();
const laboratoryStore = useLaboratoryStore();
const categoryStore = useCategoryStore();
const notificationStore = useNotificationStore();

// Estado local
const selectedLab = ref('');
const selectedCategory = ref('');
const updateType = ref<'price' | 'factor' | 'both'>('price');
const percentageIncrease = ref(0);
const factorIncrease = ref(0);
const error = ref('');
const processing = ref(false);

// Productos afectados por la actualización
const affectedProducts = computed(() => {
  return productStore.products.filter(product => {
    const hasSelectedLab = product.laboratories.some(lab => 
      lab.laboratory.id === selectedLab.value
    );
    
    const matchesCategory = !selectedCategory.value || 
      product.Categoria === selectedCategory.value;
    
    // Excluir productos con precio compuesto
    const isComposite = product.laboratories.some(lab => 
      lab.details.isCompuesto
    );
    
    return hasSelectedLab && matchesCategory && !isComposite;
  });
});

// Calcular nuevo precio según el porcentaje
const calculateNewPrice = (product: ProductWithDetails) => {
  const lab = product.laboratories.find(l => l.laboratory.id === selectedLab.value);
  if (!lab) return 0;
  
  const currentPrice = lab.details.PrecioDeCosto;
  return currentPrice * (1 + percentageIncrease.value / 100);
};

// Calcular nuevo factor según el porcentaje
const calculateNewFactor = (product: ProductWithDetails) => {
  return product.Factor * (1 + factorIncrease.value / 100);
};

// Actualizar precios y/o factores
const updatePrices = async () => {
  if (updateType.value === 'price' && percentageIncrease.value === 0) {
    error.value = 'El porcentaje de incremento en precios debe ser diferente de 0';
    return;
  }

  if (updateType.value === 'factor' && factorIncrease.value === 0) {
    error.value = 'El porcentaje de incremento en factores debe ser diferente de 0';
    return;
  }

  if (!selectedLab.value) {
    error.value = 'Debe seleccionar un laboratorio';
    return;
  }

  processing.value = true;
  error.value = '';

  try {
    for (const product of affectedProducts.value) {
      const updatedProduct = { ...product };

      if (updateType.value === 'price' || updateType.value === 'both') {
        updatedProduct.laboratories = product.laboratories.map(lab => {
          if (lab.laboratory.id === selectedLab.value) {
            return {
              ...lab,
              details: {
                ...lab.details,
                PrecioDeCosto: calculateNewPrice(product),
                UltimaActualizacion: new Date()
              }
            };
          }
          return lab;
        });
      }

      if (updateType.value === 'factor' || updateType.value === 'both') {
        updatedProduct.Factor = calculateNewFactor(product);
      }

      await productStore.updateProduct(product.id!, updatedProduct);
    }

    notificationStore.success(`Se actualizaron ${affectedProducts.value.length} productos correctamente`);
    percentageIncrease.value = 0;
    factorIncrease.value = 0;
    error.value = '';
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Error al actualizar los precios';
    notificationStore.error('Error al actualizar los precios');
  } finally {
    processing.value = false;
  }
};
</script>

<template>
  <div>
    <h2 class="text-2xl font-bold text-slate-800 mb-6">Modificación Masiva de Precios</h2>
    
    <div class="space-y-6 max-w-4xl">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">Laboratorio</label>
          <select v-model="selectedLab" class="input-field">
            <option value="">Seleccionar laboratorio</option>
            <option 
              v-for="lab in laboratoryStore.laboratories" 
              :key="lab.id" 
              :value="lab.id"
            >
              {{ lab.Nombre }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-slate-700 mb-2">Categoría (Opcional)</label>
          <select v-model="selectedCategory" class="input-field">
            <option value="">Todas las categorías</option>
            <option 
              v-for="[id, label] in Object.entries(categoryStore.categories)" 
              :key="id" 
              :value="id"
            >
              {{ label }}
            </option>
          </select>
        </div>
      </div>

      <UpdateOptions
        v-model:update-type="updateType"
        v-model:percentage-increase="percentageIncrease"
        v-model:factor-increase="factorIncrease"
      />

      <div v-if="error" class="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
        {{ error }}
      </div>

      <!-- Vista previa de cambios -->
      <div v-if="affectedProducts.length > 0" class="mt-8">
        <h3 class="text-lg font-semibold text-slate-800 mb-4">Vista Previa de Cambios</h3>
        <div class="overflow-x-auto rounded-lg border border-slate-200">
          <table class="table-elegant">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoría</th>
                <th v-if="updateType !== 'factor'">Precio Actual</th>
                <th v-if="updateType !== 'factor'">Nuevo Precio</th>
                <th v-if="updateType !== 'price'">Factor Actual</th>
                <th v-if="updateType !== 'price'">Nuevo Factor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in affectedProducts" :key="product.id">
                <td class="font-medium">{{ product.Nombre }}</td>
                <td>{{ categoryStore.categories[product.Categoria] }}</td>
                <td v-if="updateType !== 'factor'">
                  ${{ product.laboratories.find(l => l.laboratory.id === selectedLab)?.details.PrecioDeCosto.toLocaleString() }}
                </td>
                <td v-if="updateType !== 'factor'" class="text-indigo-600 font-medium">
                  ${{ calculateNewPrice(product).toLocaleString() }}
                </td>
                <td v-if="updateType !== 'price'">
                  {{ product.Factor.toFixed(2) }}
                </td>
                <td v-if="updateType !== 'price'" class="text-indigo-600 font-medium">
                  {{ calculateNewFactor(product).toFixed(2) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex justify-end mt-6">
        <button 
          @click="updatePrices" 
          class="btn-primary"
          :disabled="processing || affectedProducts.length === 0 || 
            (updateType === 'price' && percentageIncrease === 0) || 
            (updateType === 'factor' && factorIncrease === 0) ||
            (updateType === 'both' && percentageIncrease === 0 && factorIncrease === 0)"
        >
          {{ processing ? 'Actualizando...' : 'Actualizar Productos' }}
        </button>
      </div>
    </div>
  </div>
</template>